import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Divider,
  Button,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Tooltip,
  Collapse,
  Stack,
  useTheme,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  CircularProgress,
  Menu,
  Avatar,
  Skeleton,
  TableFooter,
  TableSortLabel,
} from '@mui/material';
import {
  Receipt,
  AttachMoney,
  Warning,
  CheckCircle,
  HourglassEmpty,
  Add,
  Payment as PaymentIcon,
  FilterList,
  Cancel as CancelIcon,
  ExpandMore,
  ExpandLess,
  Clear,
  CalendarMonth,
  Receipt as ReceiptIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Sync as SyncIcon,
  Print as PrintIcon,
  ReceiptLong as ReceiptLongIcon,
  SwapHoriz as RenegociarIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { format, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, subDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import SysDateRangePicker from '../../../components/SysDateRangePicker';
import { Invoice } from '../../../types';
import { InvoiceFormData, InvoiceFilters } from '../../../services/invoiceService';
import invoiceService from '../../../services/invoiceService';
import InvoiceMultipleForm from '../../../components/forms/InvoiceMultipleForm';
import InvoicePaymentForm from '../../../components/forms/InvoicePaymentForm';
import InvoiceEditForm from '../../../components/forms/InvoiceEditForm';
import ProcessBoletoModal from '../../../components/modals/ProcessBoletoModal';
import EmitirNfcomModal from '../../../components/modals/EmitirNfcomModal';
import InvoiceViewModal from '../../../components/modals/InvoiceViewModal';
import { useRelatorioCores } from '../../relatorios';
import ReciboModal from '../../../components/modals/ReciboModal';
import GatewaySelect, { GATEWAY_FILTER_SEM_VALUE } from '../../../components/selects/GatewaySelect';
import FormaPgtoSelect from '../../../components/selects/FormaPgtoSelect';
import { formatarData, formatarDataHora, formatarValor } from '../../../utils/formatToView';
import { useInvoiceActions } from '../../../hooks/useInvoiceActions';
import api from '../../../services/api';
import { InvoiceStatusChip } from '../../../utils/faturaStatusUtils';


// Função utilitária para obter o primeiro e último dia do mês atual
const getCurrentMonthRange = () => {
  const now = new Date();
  return {
    startDate: startOfMonth(now),
    endDate: endOfMonth(now)
  };
};

// Opções de filtros rápidos
const quickFilterOptions = [
  {
    label: 'Hoje',
    value: 'today',
    getRange: () => ({
      startDate: new Date(),
      endDate: new Date()
    })
  },
  {
    label: 'Mês Atual',
    value: 'current-month',
    getRange: () => getCurrentMonthRange()
  },
  {
    label: 'Últimos 30 dias',
    value: 'last-30',
    getRange: () => ({
      startDate: subDays(new Date(), 30),
      endDate: new Date()
    })
  },
  {
    label: 'Últimos 3 meses',
    value: 'last-3-months',
    getRange: () => ({
      startDate: subMonths(new Date(), 3),
      endDate: new Date()
    })
  },
  {
    label: 'Ano Atual',
    value: 'current-year',
    getRange: () => ({
      startDate: startOfYear(new Date()),
      endDate: endOfYear(new Date())
    })
  },
  {
    label: 'Todos os Períodos',
    value: 'all',
    getRange: () => ({
      startDate: null,
      endDate: null
    })
  }
];

// Opções para filtros select
const tipoOptions = [
  { value: '', label: 'Todos os tipos' },
  { value: 'mensalidade', label: 'Mensalidade' },
  { value: 'servicos', label: 'Serviços' },
  { value: 'outros', label: 'Outros' }
];

const statusOptions = [
  { value: '', label: 'Todos os status' },
  { value: 'aberto', label: 'Aberto' },
  { value: 'pago', label: 'Pago' },
  { value: 'vencido', label: 'Vencido' },
  { value: 'parcialmente_pago', label: 'Parcialmente Pago' },
  { value: 'cancelado', label: 'Cancelado' }
];

interface DateFilterState {
  startDate: Date | null;
  endDate: Date | null;
  quickFilter: string;
}

interface DateRangeFilterState {
  startDate: Date | null;
  endDate: Date | null;
}

interface SelectFiltersState {
  tipo: string;
  status: string;
  gateway_id: string;
  invoiceId: string;
  status_cliente: string;
  forma_pagamento: string;
  /** Quando true, filtra faturas pagas dentro do prazo (data_pagamento <= data_vencimento) */
  pago_no_prazo: boolean;
}

// --- Conteúdo da lista de faturas (aba ou página dedicada) ---

const FaturasTabContent: React.FC = () => {
  const theme = useTheme();
  const cores = useRelatorioCores();
  const faturasColor = cores.primario;
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [stats, setStats] = useState({
    pending: 0,
    paid: 0,
    overdue: 0,
    total: 0,
    monthlyRevenue: 0,
    pendingTotal: 0,
    paidTotal: 0,
    overdueTotal: 0,
    cancelled: 0,
    cancelledTotal: 0,
    totalValorValue: 0
  });
  const [paymentData, setPaymentData] = useState({
    paymentMethod: '',
    transactionId: ''
  });
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'warning'; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newPaymentDialogOpen, setNewPaymentDialogOpen] = useState(false);
  const [selectedPaymentInvoice, setSelectedPaymentInvoice] = useState<Invoice | null>(null);

  // Estados para impressão
  const [selectedInvoiceForPrint, setSelectedInvoiceForPrint] = useState<Invoice | null>(null);

  // Estados para filtros de data
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [dueDateFilters, setDueDateFilters] = useState<DateFilterState>(() => {
    const currentMonth = getCurrentMonthRange();
    return {
      startDate: currentMonth.startDate,
      endDate: currentMonth.endDate,
      quickFilter: 'current-month'
    };
  });

  // Estado para filtro de data de pagamento
  const [paymentDateFilters, setPaymentDateFilters] = useState<DateRangeFilterState>({
    startDate: null,
    endDate: null
  });

  // Estado para filtro de data de criação
  const [creationDateFilters, setCreationDateFilters] = useState<DateRangeFilterState>({
    startDate: null,
    endDate: null
  });

  // Estados para filtros select
  const [selectFilters, setSelectFilters] = useState<SelectFiltersState>({
    tipo: '',
    status: '',
    gateway_id: '',
    invoiceId: '',
    status_cliente: '',
    forma_pagamento: '',
    pago_no_prazo: false
  });

  // Estados para ordenação (seguindo padrão do relatório)
  const [orderBy, setOrderBy] = useState<string>('id');
  const [orderDir, setOrderDir] = useState<'asc' | 'desc'>('desc');

  // Estados locais para controle de modais e ações (anteriormente no InvoicesTable)
  const [editFormOpen, setEditFormOpen] = useState(false);
  const [selectedInvoiceForEdit, setSelectedInvoiceForEdit] = useState<Invoice | null>(null);
  const [processBoletoOpen, setProcessBoletoOpen] = useState(false);
  const [emitirNfcomOpen, setEmitirNfcomOpen] = useState(false);
  const [selectedInvoiceForNfcom, setSelectedInvoiceForNfcom] = useState<Invoice | null>(null);
  const [reciboOpen, setReciboOpen] = useState(false);
  const [selectedInvoiceForRecibo, setSelectedInvoiceForRecibo] = useState<number | null>(null);
  const [anchorEls, setAnchorEls] = useState<{ [key: number]: HTMLElement | null }>({});
  const [currentInvoiceForModal, setCurrentInvoiceForModal] = useState<Invoice | null>(null);
  /** Faturas para ProcessBoletoModal em massa (null = modo único com currentInvoiceForModal) */
  const [bulkBoletoInvoices, setBulkBoletoInvoices] = useState<Invoice[] | null>(null);

  const showContractColumn = true;
  const showClientColumn = true;
  const showGatewayColumn = false;

  const columns = [
    { id: 'id', label: 'ID', sortable: true },
    { id: 'cliente', label: 'Cliente', sortable: false, visible: showClientColumn },
    { id: 'contrato', label: 'Contrato', sortable: false, visible: showContractColumn },
    { id: 'tipo', label: 'Tipo', sortable: false },
    { id: 'data_vencimento', label: 'Vencimento', sortable: true },
    { id: 'valor', label: 'Valor', sortable: true },
    { id: 'status', label: 'Status', sortable: true },
    { id: 'data_pagamento', label: 'Pagamento', sortable: true },
    { id: 'valor_pago', label: 'Valor Pago', sortable: true },
    { id: 'gateway', label: 'Gateway', sortable: false, visible: showGatewayColumn },
  ];



  // Função para aplicar filtro rápido
  const applyQuickFilter = (filterValue: string) => {
    const selectedFilter = quickFilterOptions.find(opt => opt.value === filterValue);
    if (selectedFilter) {
      const range = selectedFilter.getRange();
      setDueDateFilters({
        startDate: range.startDate,
        endDate: range.endDate,
        quickFilter: filterValue
      });
    }
  };

  // Função para limpar filtros
  const clearFilters = () => {
    const currentMonth = getCurrentMonthRange();
    setDueDateFilters({
      startDate: null,
      endDate: null,
      quickFilter: ''
    });
    setPaymentDateFilters({
      startDate: null,
      endDate: null
    });
    setCreationDateFilters({
      startDate: null,
      endDate: null
    });
    setSelectFilters({
      tipo: '',
      status: '',
      gateway_id: '',
      invoiceId: '',
      status_cliente: '',
      forma_pagamento: '',
      pago_no_prazo: false
    });
  };

  // Função para preparar filtros da API
  const prepareFilters = (): InvoiceFilters => {
    const filters: InvoiceFilters = {
      page: page + 1,
      limit: rowsPerPage
    };

    // Adicionar filtros de data de vencimento se definidos
    if (dueDateFilters.startDate) {
      filters.data_inicio = format(dueDateFilters.startDate, 'yyyy-MM-dd');
    }
    if (dueDateFilters.endDate) {
      filters.data_fim = format(dueDateFilters.endDate, 'yyyy-MM-dd');
    }

    // Adicionar filtros de data de pagamento se definidos
    if (paymentDateFilters.startDate) {
      filters.data_pagamento_inicio = format(paymentDateFilters.startDate, 'yyyy-MM-dd');
    }
    if (paymentDateFilters.endDate) {
      filters.data_pagamento_fim = format(paymentDateFilters.endDate, 'yyyy-MM-dd');
    }

    // Adicionar filtros de data de criação se definidos
    if (creationDateFilters.startDate) {
      filters.data_criacao_inicio = format(creationDateFilters.startDate, 'yyyy-MM-dd');
    }
    if (creationDateFilters.endDate) {
      filters.data_criacao_fim = format(creationDateFilters.endDate, 'yyyy-MM-dd');
    }

    // Adicionar filtros select se definidos
    if (selectFilters.tipo) {
      filters.tipo = selectFilters.tipo;
    }
    if (selectFilters.status) {
      filters.status = selectFilters.status;
    }
    if (selectFilters.gateway_id === GATEWAY_FILTER_SEM_VALUE) {
      filters.sem_gateway = true;
    } else if (selectFilters.gateway_id) {
      filters.gateway_id = parseInt(selectFilters.gateway_id, 10);
    }

    if (selectFilters.status_cliente) {
      filters.status_cliente = selectFilters.status_cliente;
    }

    if (selectFilters.forma_pagamento) {
      filters.forma_pagamento = selectFilters.forma_pagamento;
    }

    // Filtro: pago no prazo (data_pagamento <= data_vencimento)
    if (selectFilters.pago_no_prazo) {
      filters.pago_no_prazo = true;
      // Garantir que status seja 'pago' ao usar este filtro
      filters.status = 'pago';
    }

    // Nota: Filtro por ID será tratado na queryFn abaixo
    // Configurar ordenação para API (convertendo para maiúsculo)
    filters.sortBy = orderBy;
    filters.sortOrder = orderDir.toUpperCase() as 'ASC' | 'DESC';

    return filters;
  };

  // Função para processar resposta da API e calcular estatísticas
  const processInvoicesData = (invoicesData: Invoice[]) => {
    // Calcular estatísticas
    const pendingInvoices = invoicesData.filter(inv => inv.status === 'aberto');
    const paidInvoices = invoicesData.filter(inv => inv.status === 'pago');
    const overdueInvoices = invoicesData.filter(inv =>
      inv.status === 'vencido' && invoiceService.isOverdue(inv.data_vencimento, inv.status)
    );

    // Calcular totais
    const pendingTotal = pendingInvoices.reduce((sum, inv) => {
      const valor = Number(inv.valor) || 0;
      const acrescimo = Number(inv.valor) || 0;
      const desconto = Number(inv.desconto) || 0;
      return sum + invoiceService.calculateTotal(valor, acrescimo, desconto);
    }, 0);

    const paidTotal = paidInvoices.reduce((sum, inv) => {
      const valor = Number(inv.valor) || 0;
      const acrescimo = Number(inv.acrescimo) || 0;
      const desconto = Number(inv.desconto) || 0;
      return sum + invoiceService.calculateTotal(valor, acrescimo, desconto);
    }, 0);

    const overdueTotal = overdueInvoices.reduce((sum, inv) => {
      const valor = Number(inv.valor) || 0;
      const acrescimo = Number(inv.acrescimo) || 0;
      const desconto = Number(inv.desconto) || 0;
      return sum + invoiceService.calculateTotal(valor, acrescimo, desconto);
    }, 0);

    return {
      pending: pendingInvoices.length,
      paid: paidInvoices.length,
      overdue: overdueInvoices.length,
      total: invoicesData.length,
      monthlyRevenue: paidTotal,
      pendingTotal,
      paidTotal,
      overdueTotal,
      cancelled: invoicesData.filter(inv => inv.status === 'cancelado').length,
      cancelledTotal: invoicesData.filter(inv => inv.status === 'cancelado').reduce((sum, inv) => sum + (Number(inv.valor) || 0), 0),
      totalValorValue: invoicesData.reduce((sum, inv) => sum + (Number(inv.valor) || 0), 0)
    };
  };

  // Query principal para buscar faturas
  const {
    data: invoicesResponse,
    isLoading: loading,
    error: queryError,
    refetch
  } = useQuery({
    queryKey: ['invoices', page, rowsPerPage, dueDateFilters, paymentDateFilters, creationDateFilters, selectFilters, orderBy, orderDir, selectFilters.pago_no_prazo],
    queryFn: async () => {
      // Se houver filtro por ID, buscar diretamente pelo ID
      if (selectFilters.invoiceId && selectFilters.invoiceId.trim() !== '') {
        const invoiceId = parseInt(selectFilters.invoiceId.trim(), 10);
        if (!isNaN(invoiceId)) {
          try {
            const response = await invoiceService.getInvoiceById(invoiceId);
            if (response.success && response.data) {
              return {
                invoices: [response.data],
                totalCount: 1,
                stats: processInvoicesData([response.data])
              };
            } else {
              return {
                invoices: [],
                totalCount: 0,
                stats: {
                  pending: 0,
                  paid: 0,
                  overdue: 0,
                  total: 0,
                  monthlyRevenue: 0,
                  pendingTotal: 0,
                  paidTotal: 0,
                  overdueTotal: 0,
                  cancelled: 0,
                  cancelledTotal: 0,
                  totalValorValue: 0
                }
              };
            }
          } catch (error) {
            // Se não encontrar, retornar lista vazia
            return {
              invoices: [],
              totalCount: 0,
              stats: {
                pending: 0,
                paid: 0,
                overdue: 0,
                total: 0,
                monthlyRevenue: 0,
                pendingTotal: 0,
                paidTotal: 0,
                overdueTotal: 0,
                cancelled: 0,
                cancelledTotal: 0,
                totalValorValue: 0
              }
            };
          }
        }
      }

      // Busca normal com filtros
      const filters = prepareFilters();
      const response = await invoiceService.getInvoices(filters);

      // Verificar se a resposta tem formato ApiResponse ou é um array direto
      let invoicesData: Invoice[] = [];
      let totalCountData = 0;

      if (response && typeof response === 'object') {
        const resp = response as any;
        if (resp.success) {
          invoicesData = resp.data || [];
          totalCountData = resp.pagination?.total || 0;

          if (resp.summary) {
            const s = resp.summary;
            return {
              invoices: invoicesData,
              totalCount: totalCountData,
              stats: {
                total: Number(s.total_count) || 0,
                pending: Number(s.total_aberto_count) || 0,
                paid: Number(s.total_pago_count) || 0,
                overdue: Number(s.total_vencido_count) || 0,
                monthlyRevenue: Number(s.total_pago) || 0,
                pendingTotal: Number(s.total_aberto) || 0,
                paidTotal: Number(s.total_pago) || 0,
                overdueTotal: Number(s.total_vencido) || 0,
                cancelled: Number(s.total_cancelado_count) || 0,
                cancelledTotal: Number(s.total_cancelado) || 0,
                totalValorValue: Number(s.total_valor) || 0
              }
            };
          }
        } else if (resp.data && Array.isArray(resp.data)) {
          invoicesData = resp.data;
          totalCountData = resp.pagination?.total || resp.data.length;
        } else if (Array.isArray(response)) {
          invoicesData = response;
          totalCountData = response.length;
        }
      }

      return {
        invoices: invoicesData,
        totalCount: totalCountData,
        stats: processInvoicesData(invoicesData)
      };
    },
    staleTime: 0, // Sem cache - sempre busca dados frescos
    refetchOnMount: true, // Refaz busca ao montar componente
    refetchOnWindowFocus: true, // Refaz busca ao focar na janela
    refetchOnReconnect: true, // Refaz busca ao reconectar
    refetchInterval: 10000, // Refetch a cada 10 segundos
    refetchIntervalInBackground: true,
  });

  // Atualizar estado local quando a query retornar dados
  useEffect(() => {
    if (invoicesResponse) {
      setInvoices(invoicesResponse.invoices);
      setTotalCount(invoicesResponse.totalCount);
      setStats(invoicesResponse.stats);
    }
  }, [invoicesResponse]);

  // Tratar erros da query
  useEffect(() => {
    if (queryError) {
      console.error('Erro ao carregar faturas:', queryError);
      showAlert('error', 'Erro ao carregar faturas');
    }
  }, [queryError]);

  const showAlert = (type: 'success' | 'error' | 'warning', message: string) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  // Hook centralizado para ações das faturas
  const {
    formOpen,
    selectedInvoice,
    bulkCancelMode,
    bulkPrintMode,
    bulkEditMode,
    selectedInvoices,
    selectedInvoicesData,
    handleSelectInvoice,
    handleMassCancel,
    handleMassPrint,
    handleMassEdit,
    setFormOpen,
    setSelectedInvoice,
    hasSelectedInvoices,
    // Novos campos do useInvoiceActions (anteriormente no InvoicesTable)
    viewModalOpen,
    viewInvoice,
    handleViewInvoice,
    handleCloseViewModal,
    handleCheckGatewayStatus,
  } = useInvoiceActions({
    showAlert,
    loadInvoices: async () => {
      await refetch();
    }
  });

  // Funções de manipulação (anteriormente no InvoicesTable)
  const handleEditClick = (invoice: Invoice) => {
    setSelectedInvoiceForEdit(invoice);
    setEditFormOpen(true);
    handleCloseViewModal();
  };

  const handleEditClose = () => {
    setEditFormOpen(false);
    setSelectedInvoiceForEdit(null);
  };

  const handleEditSave = async (invoiceData: InvoiceFormData) => {
    if (!selectedInvoiceForEdit) return;
    try {
      const response = await invoiceService.updateInvoice(selectedInvoiceForEdit.id, invoiceData);
      if (response.success) {
        showAlert('success', 'Fatura atualizada com sucesso!');
        handleEditClose();
        refetch();
      }
    } catch (error: any) {
      console.error('Erro ao atualizar fatura:', error);
    }
  };

  const handlePaymentFromView = (invoice: Invoice) => {
    handleCloseViewModal();
    handleReceivePaymentClick(invoice);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, invoiceId: number) => {
    setAnchorEls(prev => ({ ...prev, [invoiceId]: event.currentTarget }));
  };

  const handleMenuClose = (invoiceId: number) => {
    setAnchorEls(prev => ({ ...prev, [invoiceId]: null }));
  };

  const handleProcessBoletoClick = (invoice: Invoice) => {
    setBulkBoletoInvoices(null);
    setCurrentInvoiceForModal(invoice);
    setProcessBoletoOpen(true);
  };

  const handleProcessBoletoClose = () => {
    setProcessBoletoOpen(false);
    setCurrentInvoiceForModal(null);
    setBulkBoletoInvoices(null);
  };

  const handlePrintSingleInvoiceWithSweet = async (invoice: Invoice) => {
    try {
      const printType = await handleMassPrint();
      if (printType === 'fatura' || printType === 'carne' || printType === 'capa_carne') {
        Swal.fire({
          title: 'Gerando PDF...',
          text: 'Aguarde enquanto geramos o documento',
          allowOutsideClick: false,
          didOpen: () => { Swal.showLoading(); }
        });
        const blob = await invoiceService.imprimirPDF([invoice.id], printType as 'fatura' | 'carne' | 'capa_carne');
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        Swal.close();
      }
    } catch (error) {
      console.error('Erro ao processar impressão individual:', error);
      showAlert('error', 'Erro ao gerar PDF da fatura');
      Swal.close();
    }
  };

  const handleEmitirNfcomClick = async (invoice: Invoice) => {
    setSelectedInvoiceForNfcom(invoice);
    setEmitirNfcomOpen(true);
  };

  const handleEmitirNfcomClose = () => {
    setEmitirNfcomOpen(false);
    setSelectedInvoiceForNfcom(null);
  };

  const handleReciboClick = (invoice: Invoice) => {
    setSelectedInvoiceForRecibo(invoice.id);
    setReciboOpen(true);
  };

  const handleReciboClose = () => {
    setReciboOpen(false);
    setSelectedInvoiceForRecibo(null);
  };

  const calculateTotal = (invoice: Invoice) => {
    const valor = Number(invoice.valor) || 0;
    const acrescimo = Number(invoice.acrescimo) || 0;
    const desconto = Number(invoice.desconto) || 0;
    return invoiceService.calculateTotal(valor, acrescimo, desconto);
  };

  const canProcessBoleto = (invoice: Invoice) => {
    const today = new Date();
    const dueDate = new Date(invoice.data_vencimento + 'T23:59:59');
    return invoice.status === 'aberto' && dueDate >= today;
  };

  const faturasElegiveisBoletoEmMassa = invoices.filter(
    (inv) =>
      selectedInvoices.includes(inv.id) &&
      canProcessBoleto(inv) &&
      inv.gateway_id_externo === null
  );

  const handleBulkProcessBoletoClick = () => {
    if (faturasElegiveisBoletoEmMassa.length === 0) {
      showAlert(
        'warning',
        'Nenhuma fatura elegível: precisam estar abertas, com vencimento hoje ou futuro e sem cobrança gerada no gateway.'
      );
      return;
    }
    setCurrentInvoiceForModal(null);
    setBulkBoletoInvoices(faturasElegiveisBoletoEmMassa);
    setProcessBoletoOpen(true);
  };

  const totalValorPagina = invoices.reduce((acc, f) => acc + (Number(f.valor) || 0), 0);
  const totalPagoPagina = invoices.reduce((acc, f) => acc + (Number((f as any).valor_pago) || 0), 0);

  const handleClientView = (clientId: number) => navigate(`/clients/${clientId}`);
  const handleContractView = (contractId: number | undefined) => { if (contractId) navigate(`/contracts/${contractId}`); };

  const handleSort = (col: string) => {
    if (orderBy === col) {
      setOrderDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(col);
      setOrderDir('desc');
    }
  };

  const handleReceivePaymentClick = (invoice: Invoice) => {
    setSelectedPaymentInvoice(invoice);
    setNewPaymentDialogOpen(true);
  };



  const handleCreateInvoice = () => {
    setSelectedInvoice(null);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    // Desativar modo de edição em massa quando o formulário for fechado
    if (bulkEditMode) {
      //handleCancelBulkMode();
    }
  };

  const handleSaveInvoice = async () => {
    setFormOpen(false);
    // Aguardar um momento para garantir que as faturas foram persistidas no BD
    setTimeout(() => {
      refetch(); // Recarregar faturas do BD
    }, 500);
  };

  const handleBulkPrintClick = async () => {
    try {
      if (selectedInvoices.length === 0) {
        showAlert('warning', 'Nenhuma fatura selecionada para imprimir.');
        return;
      }

      const printType = await handleMassPrint();

      if (printType === 'fatura' || printType === 'carne' || printType === 'capa_carne') {
        const result = await invoiceService.imprimirPDF(selectedInvoices, printType);

        if (result && result.size > 0) {
          const url = window.URL.createObjectURL(result);
          window.open(url, '_blank');

          // Limpar seleção após imprimir
          //handleCancelBulkMode();
          showAlert('success', 'PDF gerado com sucesso!');
        } else {
          showAlert('error', 'Falha ao gerar o PDF. O arquivo retornou vazio.');
        }
      }
    } catch (error: any) {
      console.error('Erro ao processar impressão em massa:', error);
      showAlert('error', error.message || 'Erro ao processar impressão em massa');
    }
  };

  const kpisData = React.useMemo(() => [
    { titulo: 'Total Faturas', valor: stats.total.toLocaleString('pt-BR'), icon: <Receipt />, cor: theme.palette.primary.main },
    { titulo: 'Valor Total', valor: formatarValor(stats.totalValorValue), icon: <AttachMoney />, cor: theme.palette.secondary.main },
    { titulo: 'Total Recebido', valor: formatarValor(stats.monthlyRevenue), icon: <CheckCircle />, cor: theme.palette.success.main },
    { titulo: 'Em Aberto', valor: formatarValor(stats.pendingTotal), icon: <HourglassEmpty />, cor: theme.palette.info.main },
    { titulo: 'Vencido', valor: formatarValor(stats.overdueTotal), icon: <Warning />, cor: theme.palette.error.main },
    { titulo: 'Cancelado', valor: formatarValor(stats.cancelledTotal), icon: <CancelIcon />, cor: '#9e9e9e' },
  ], [stats, theme.palette, formatarValor]);

  return (
    <Box sx={{ p: 0.5 }}>
      {/* Alert */}
      {alert && (
        <Alert severity={alert.type} sx={{ mb: 1.5 }}>
          {alert.message}
        </Alert>
      )}
      {/* Filtros */}
      <Paper elevation={2} sx={{ mb: 2 }}>
        <Box p={1.5}>
          {/* Filtros Rápidos */}
          <Grid container spacing={2} mb={1.5}>
            <Grid item xs={12} md={8}>
              <Typography variant="subtitle2" color="text.secondary" mb={0.5} sx={{ fontSize: '0.8rem' }}>
                Períodos Predefinidos
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                {quickFilterOptions.map((option) => (
                  <Chip
                    key={option.value}
                    label={option.label}
                    variant={dueDateFilters.quickFilter === option.value ? "filled" : "outlined"}
                    color={dueDateFilters.quickFilter === option.value ? "primary" : "default"}
                    onClick={() => applyQuickFilter(option.value)}
                    clickable
                    sx={{ fontSize: '0.75rem', height: 24 }}
                  />
                ))}
              </Stack>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary" mb={0.5} sx={{ fontSize: '0.8rem' }}>
                Filtros Predefinidos
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                <Chip
                  label="Clientes Inativos"
                  variant={selectFilters.status_cliente === 'inativo' ? "filled" : "outlined"}
                  color={selectFilters.status_cliente === 'inativo' ? "error" : "default"}
                  onClick={() => {
                    setSelectFilters(prev => ({
                      ...prev,
                      status_cliente: prev.status_cliente === 'inativo' ? '' : 'inativo'
                    }));
                  }}
                  clickable
                  sx={{ fontSize: '0.75rem', height: 24 }}
                />
                <Chip
                  label="✅ Pago no Prazo"
                  variant={selectFilters.pago_no_prazo ? "filled" : "outlined"}
                  color={selectFilters.pago_no_prazo ? "success" : "default"}
                  onClick={() => {
                    setSelectFilters(prev => ({
                      ...prev,
                      pago_no_prazo: !prev.pago_no_prazo,
                      // Ao ativar, garantir que status não conflite
                      status: !prev.pago_no_prazo ? 'pago' : prev.status
                    }));
                  }}
                  clickable
                  sx={{ fontSize: '0.75rem', height: 24 }}
                />
              </Stack>
            </Grid>
          </Grid>

          {/* Filtros de Período */}
          <Box mb={1.5}>
            <Typography variant="subtitle2" color="text.secondary" mb={1} sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
              Filtros de Período
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={4}>
                <SysDateRangePicker
                  label="Data de Criação"
                  onChange={(range: { startDate: Date | null; endDate: Date | null }) => {
                    setCreationDateFilters({
                      startDate: range.startDate,
                      endDate: range.endDate
                    });
                  }}
                  value={{
                    startDate: creationDateFilters.startDate,
                    endDate: creationDateFilters.endDate
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SysDateRangePicker
                  label="Data de vencimento"
                  value={{
                    startDate: dueDateFilters.startDate,
                    endDate: dueDateFilters.endDate
                  }}
                  onChange={(range: { startDate: Date | null; endDate: Date | null }) => {
                    setDueDateFilters(prev => ({
                      ...prev,
                      startDate: range.startDate,
                      endDate: range.endDate,
                      quickFilter: 'custom'
                    }));
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SysDateRangePicker
                  label="Data de Pagamento"
                  onChange={(range: { startDate: Date | null; endDate: Date | null }) => {
                    setPaymentDateFilters({
                      startDate: range.startDate,
                      endDate: range.endDate
                    });
                  }}
                  value={{
                    startDate: paymentDateFilters.startDate,
                    endDate: paymentDateFilters.endDate
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Filtros Select */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" mb={1} sx={{ fontSize: '0.8rem' }}>
              Filtros Avançados (Opcionais)
            </Typography>

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="ID da Fatura"
                  type="number"
                  value={selectFilters.invoiceId}
                  onChange={(e) => setSelectFilters(prev => ({ ...prev, invoiceId: e.target.value }))}
                  placeholder="Ex: 12345"
                  InputProps={{
                    inputProps: { min: 1 }
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={selectFilters.tipo}
                    onChange={(e) => setSelectFilters(prev => ({ ...prev, tipo: e.target.value }))}
                    label="Tipo"
                  >
                    {tipoOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={selectFilters.status}
                    onChange={(e) => setSelectFilters(prev => ({ ...prev, status: e.target.value }))}
                    label="Status"
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormaPgtoSelect
                  value={selectFilters.forma_pagamento}
                  onChange={(val) => setSelectFilters(prev => ({ ...prev, forma_pagamento: val }))}
                  size="small"
                  allowClear
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <GatewaySelect
                  value={
                    selectFilters.gateway_id === GATEWAY_FILTER_SEM_VALUE
                      ? GATEWAY_FILTER_SEM_VALUE
                      : selectFilters.gateway_id
                        ? Number(selectFilters.gateway_id)
                        : ''
                  }
                  onChange={(gatewayId) => {
                    setSelectFilters((prev) => ({
                      ...prev,
                      gateway_id: gatewayId != null ? String(gatewayId) : ''
                    }));
                  }}
                  onSemGatewayFilter={() =>
                    setSelectFilters((prev) => ({
                      ...prev,
                      gateway_id: GATEWAY_FILTER_SEM_VALUE
                    }))
                  }
                  label="Gateway"
                  size="small"
                  showOnlyActive={false}
                  required={false}
                  includeSemGatewayOption
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={clearFilters}
                  size="small"
                  fullWidth
                  sx={{ fontSize: '0.8rem', px: 1.5, py: 0.5, height: '100%' }}
                >
                  Limpar Filtros
                </Button>
              </Grid>
            </Grid>
          </Box>

        </Box>
      </Paper>

      {/* Resumo Financeiro */}
      <Grid container spacing={1.5} mb={2}>
        {kpisData.map((kpi: any) => (
          <Grid item xs={6} sm={4} md={2} key={kpi.titulo}>
            <Paper
              elevation={2}
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                transition: 'box-shadow 0.2s, transform 0.15s',
                '&:hover': { boxShadow: theme.shadows[6], transform: 'translateY(-2px)' },
              }}
            >
              <Box sx={{ height: 3, background: `linear-gradient(90deg, ${kpi.cor} 0%, ${alpha(kpi.cor, 0.4)} 100%)` }} />
              <Box sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {kpi.titulo}
                  </Typography>
                  <Avatar sx={{ bgcolor: alpha(kpi.cor, 0.12), color: kpi.cor, width: 28, height: 28 }}>
                    {React.cloneElement(kpi.icon as React.ReactElement, { sx: { fontSize: 15 } })}
                  </Avatar>
                </Box>
                {loading ? (
                  <Skeleton width="60%" height={28} />
                ) : (
                  <Typography variant="h6" fontWeight={800} color="text.primary" sx={{ lineHeight: 1.1, fontSize: '1rem' }}>
                    {kpi.valor}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Seção de Faturas: título + ações na mesma linha acima da tabela */}
      <Paper elevation={2}>
        <Box p={1.5}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: 'wrap',
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              gap: 1.5,
              mb: 1,
            }}
          >
            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem', m: 0 }}>
              Lista de Faturas
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              justifyContent={{ xs: 'stretch', sm: 'flex-end' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 0 }}
            >
              <Button
                variant="outlined"
                color="error"
                disabled={!hasSelectedInvoices}
                onClick={() => handleMassCancel(selectedInvoices)}
                size="small"
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.78rem',
                  borderRadius: 1.5,
                }}
              >
                Cancelamento em Massa {hasSelectedInvoices && `(${selectedInvoices.length})`}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                disabled={!hasSelectedInvoices}
                onClick={handleBulkPrintClick}
                size="small"
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.78rem',
                  borderRadius: 1.5,
                }}
              >
                Imprimir em Massa {hasSelectedInvoices && `(${selectedInvoices.length})`}
              </Button>
              <Button
                variant="outlined"
                color="info"
                disabled={faturasElegiveisBoletoEmMassa.length === 0}
                onClick={handleBulkProcessBoletoClick}
                size="small"
                startIcon={<ReceiptIcon sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.78rem',
                  borderRadius: 1.5,
                }}
              >
                Processar boleto em massa
                {faturasElegiveisBoletoEmMassa.length > 0 && ` (${faturasElegiveisBoletoEmMassa.length})`}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                disabled={!hasSelectedInvoices}
                onClick={handleMassEdit}
                size="small"
                sx={{
                  display: { xs: 'none', sm: 'none' },
                  visibility: 'hidden',
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.78rem',
                  borderRadius: 1.5,
                }}
              >
                Editar em Massa {hasSelectedInvoices && `(${selectedInvoices.length})`}
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<Add sx={{ fontSize: 16 }} />}
                onClick={() => {
                  setSelectedInvoice(null);
                  setFormOpen(true);
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.78rem',
                  borderRadius: 1.5,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: theme.shadows[2] },
                }}
              >
                Nova Fatura
              </Button>
            </Stack>
          </Box>
          <Divider sx={{ mb: 1 }} />

          {/* Tabela de Faturas */}
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow
                  sx={{
                    '& .MuiTableCell-head': {
                      bgcolor: (t) => `color-mix(in srgb, ${faturasColor} 12%, ${t.palette.background.paper})`,
                      fontWeight: 700,
                      fontSize: '0.73rem',
                      py: 0.8,
                      borderBottom: `2px solid ${alpha(faturasColor, 0.2)}`,
                    },
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      indeterminate={selectedInvoices.length > 0 && selectedInvoices.length < invoices.length}
                      checked={selectedInvoices.length > 0 && selectedInvoices.length === invoices.length}
                      onChange={(e) => {
                        const faturasElegiveis = invoices.filter(invoice => invoice.status !== 'pago' && invoice.status !== 'cancelado');
                        const faturasElegiveisIds = faturasElegiveis.map(invoice => invoice.id);
                        const todasElegiveisSelecionadas = faturasElegiveisIds.every(id => selectedInvoices.includes(id));
                        if (todasElegiveisSelecionadas) {
                          selectedInvoices.forEach(invoiceId => handleSelectInvoice(invoiceId, false));
                        } else {
                          invoices.forEach(invoice => { if (invoice.status !== 'pago' && invoice.status !== 'cancelado') handleSelectInvoice(invoice.id, true); });
                        }
                      }}
                    />
                  </TableCell>
                  {columns.filter(col => col.visible !== false).map((col) => (
                    <TableCell
                      key={col.id}
                      sx={{
                        py: 0.8,
                        fontSize: '0.73rem',
                        fontWeight: 700,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {col.sortable ? (
                        <TableSortLabel
                          active={orderBy === col.id}
                          direction={orderBy === col.id ? orderDir : 'asc'}
                          onClick={() => handleSort(col.id)}
                        >
                          {col.label}
                        </TableSortLabel>
                      ) : (
                        col.label
                      )}
                    </TableCell>
                  ))}
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={13} align="center"><Skeleton /></TableCell>
                    </TableRow>
                  ))
                ) : error ? (
                  <TableRow><TableCell colSpan={13} align="center" ><Alert severity="error">{error}</Alert></TableCell></TableRow>
                ) : invoices.length === 0 ? (
                  <TableRow><TableCell colSpan={13} align="center" sx={{ py: 4 }}><Typography variant="body2" color="text.secondary">Nenhuma fatura encontrada</Typography></TableCell></TableRow>
                ) : (
                  invoices.map((invoice) => (
                    <TableRow key={invoice.id} hover sx={{ '&:hover': { bgcolor: alpha(faturasColor, 0.03) } }}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={selectedInvoices.includes(invoice.id)} disabled={invoice.status === 'pago' || invoice.status === 'cancelado'} size="small" onChange={(e) => handleSelectInvoice(invoice.id, e.target.checked)} />
                      </TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color="primary"
                          sx={{ fontSize: '0.78rem', cursor: 'pointer' }}
                          onClick={() => handleViewInvoice(invoice.id)}
                        >
                          #{invoice.id}
                        </Typography>
                      </TableCell>
                      {showClientColumn && (
                        <TableCell sx={{ whiteSpace: 'nowrap', py: 0.5, fontSize: '0.78rem' }}>
                          <Typography
                            variant="body2"
                            color="primary"
                            fontWeight="medium"
                            sx={{ fontSize: '0.78rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
                            onClick={() => handleClientView(invoice.cliente_id)}
                          >
                            {invoice.cliente?.nome || `#${invoice.cliente_id}`}
                          </Typography>
                        </TableCell>
                      )}
                      {showContractColumn && (
                        <TableCell sx={{ whiteSpace: 'nowrap', py: 0.5, fontSize: '0.78rem' }}>
                          <Typography
                            variant="body2"
                            color={invoice.contrato_id ? "secondary" : "text.secondary"}
                            sx={{ fontSize: '0.78rem', cursor: 'pointer' }}
                            onClick={() => handleContractView(invoice.contrato_id)}
                          >
                            {invoice.contrato?.titulo || 'Geral'}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{invoiceService.formatType(invoice.tipo)}</TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        <Typography variant="body2" color={invoiceService.isOverdue(invoice.data_vencimento, invoice.status) ? 'error' : 'text.primary'} sx={{ fontSize: '0.78rem' }}>
                          {formatarData(invoice.data_vencimento)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem', fontWeight: 600 }}>{formatarValor(calculateTotal(invoice))}</TableCell>
                      <TableCell sx={{ py: 0.5 }}><InvoiceStatusChip status={invoice.status} /></TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        {invoice.data_pagamento ? formatarData(invoice.data_pagamento) : '-'}
                      </TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem', fontWeight: 600 }}>
                        {formatarValor((invoice as any).valor_pago || 0)}
                      </TableCell>
                      {showGatewayColumn && <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{invoice.gateway?.nome || '-'}</TableCell>}
                      <TableCell align="center" sx={{ py: 0.5 }}>
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, invoice.id)}><MoreVertIcon sx={{ fontSize: '0.9rem' }} /></IconButton>
                        <Menu anchorEl={anchorEls[invoice.id]} open={Boolean(anchorEls[invoice.id])} onClose={() => handleMenuClose(invoice.id)}>
                          <MenuItem onClick={(e) => { e.stopPropagation(); handleViewInvoice(invoice.id); handleMenuClose(invoice.id); }}><VisibilityIcon sx={{ mr: 1 }} fontSize="small" />Visualizar</MenuItem>
                          {invoice.status !== 'pago' && invoice.status !== 'cancelado' && (
                            <MenuItem onClick={(e) => { e.stopPropagation(); handleEditClick(invoice); handleMenuClose(invoice.id); }}><EditIcon sx={{ mr: 1 }} fontSize="small" />Editar</MenuItem>
                          )}
                          {invoice.status !== 'pago' && invoice.status !== 'cancelado' && (
                            <MenuItem onClick={(e) => { e.stopPropagation(); handleReceivePaymentClick(invoice); handleMenuClose(invoice.id); }}><PaymentIcon sx={{ mr: 1 }} fontSize="small" />Receber Pagamento</MenuItem>
                          )}
                          {invoice.status !== 'cancelado' && (
                            <MenuItem onClick={(e) => { e.stopPropagation(); handlePrintSingleInvoiceWithSweet(invoice); handleMenuClose(invoice.id); }}><PrintIcon sx={{ mr: 1 }} fontSize="small" />Imprimir Fatura</MenuItem>
                          )}
                          {(invoice.status === 'aberto' || invoice.status === 'vencido') && (
                            <MenuItem onClick={(e) => { e.stopPropagation(); handleMassCancel([invoice.id]); handleMenuClose(invoice.id); }}><CancelIcon sx={{ mr: 1 }} fontSize="small" />Cancelar Fatura</MenuItem>
                          )}
                          {invoice.gateway_id && invoice.gateway_id_externo && (
                            <MenuItem onClick={(e) => { e.stopPropagation(); handleCheckGatewayStatus(invoice); handleMenuClose(invoice.id); }}><SyncIcon sx={{ mr: 1 }} fontSize="small" />Checar Status</MenuItem>
                          )}
                          {canProcessBoleto(invoice) && invoice.gateway_id_externo === null && (
                            <MenuItem onClick={(e) => { e.stopPropagation(); handleProcessBoletoClick(invoice); handleMenuClose(invoice.id); }}><ReceiptIcon sx={{ mr: 1 }} fontSize="small" />Processar Boleto</MenuItem>
                          )}
                          {invoice.status != 'cancelado' && invoice.nfcom_id === null && (
                            <MenuItem onClick={(e) => { e.stopPropagation(); handleEmitirNfcomClick(invoice); handleMenuClose(invoice.id); }}><ReceiptLongIcon sx={{ mr: 1 }} fontSize="small" />Emitir NFCom</MenuItem>
                          )}
                          {invoice.status === 'pago' && (
                            <MenuItem onClick={(e) => { e.stopPropagation(); handleReciboClick(invoice); handleMenuClose(invoice.id); }}><ReceiptIcon sx={{ mr: 1 }} fontSize="small" />Recibo</MenuItem>
                          )}
                          {invoice.token_publico && (
                            <MenuItem onClick={(e) => {
                              e.stopPropagation();
                              const publicUrl = `${window.location.origin}/p/${invoice.token_publico}`;
                              navigator.clipboard.writeText(publicUrl);
                              Swal.fire({
                                icon: 'success',
                                title: 'Link Copiado!',
                                text: 'O link público da fatura foi copiado para a área de transferência.',
                                timer: 2000,
                                showConfirmButton: false
                              });
                              handleMenuClose(invoice.id);
                            }}>
                              <LinkIcon sx={{ mr: 1 }} fontSize="small" />Link Público
                            </MenuItem>
                          )}
                        </Menu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
              {!loading && invoices.length > 0 && (
                <TableFooter sx={{ position: 'sticky', bottom: 0, zIndex: 2, bgcolor: 'background.paper' }}>
                  <TableRow
                    sx={{
                      '& .MuiTableCell-root': {
                        bgcolor: (t) => `color-mix(in srgb, ${faturasColor} 15%, ${t.palette.background.paper})`,
                        fontWeight: 800,
                        fontSize: '0.78rem',
                        borderTop: `2px solid ${alpha(faturasColor, 0.3)}`,
                        py: 1.2,
                        color: 'text.primary',
                      },
                    }}
                  >
                    <TableCell colSpan={showClientColumn && showContractColumn ? 6 : showClientColumn || showContractColumn ? 7 : 6} align="right" sx={{ letterSpacing: '0.05em' }}>
                      TOTAL DA PÁGINA
                    </TableCell><TableCell>
                      {formatarValor(totalValorPagina)}
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell>
                      {formatarValor(totalPagoPagina)}
                    </TableCell>
                    <TableCell colSpan={showGatewayColumn ? 2 : 1} />
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </TableContainer>

          {totalCount > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 0.8,
                px: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              }}
            >
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  const newRowsPerPage = parseInt(e.target.value, 10);
                  setRowsPerPage(newRowsPerPage);
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Linhas por pág:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
                }
                sx={{
                  width: '100%',
                  '.MuiTablePagination-toolbar': {
                    minHeight: 32,
                    flexWrap: 'nowrap',
                    justifyContent: 'center',
                    gap: 1,
                    p: 0,
                    '& .MuiTablePagination-spacer': { display: 'none' },
                  },
                  '.MuiTablePagination-selectLabel': { m: 0, fontSize: '0.78rem' },
                  '.MuiTablePagination-select': { py: 0.5, pr: 2.5, fontSize: '0.78rem' },
                  '.MuiTablePagination-displayedRows': { m: 0, fontSize: '0.78rem' },
                  '.MuiTablePagination-actions': { ml: 0.5, '& .MuiIconButton-root': { p: 0.5 } },
                }}
              />
            </Box>
          )}
        </Box>
      </Paper>

      {/* Formulário de Criação/Edição de Fatura */}
      <InvoiceMultipleForm
        open={formOpen}
        onClose={handleCloseForm}
        onSubmit={handleSaveInvoice}
        title={bulkEditMode ? `Editar ${selectedInvoices.length} Fatura(s)` : "Nova Fatura"}
        isEditMode={bulkEditMode}
        selectedInvoicesData={bulkEditMode ? selectedInvoicesData : undefined}
      />

      {/* Formulário de Pagamento */}
      {selectedPaymentInvoice && (
        <InvoicePaymentForm
          open={newPaymentDialogOpen}
          onClose={() => {
            setNewPaymentDialogOpen(false);
            setSelectedPaymentInvoice(null);
          }}
          invoice={selectedPaymentInvoice}
          onSuccess={(invoicePaymentData, invoice) => {
            console.log('✅ Pagamento processado com sucesso:', { invoicePaymentData, invoice });
            setNewPaymentDialogOpen(false);
            setSelectedPaymentInvoice(null);
            refetch();
          }}
          onError={(error) => {
            console.error('❌ Erro no pagamento:', error);
          }}
          onCancel={() => {
            console.log('🚫 Pagamento cancelado pelo usuário');
          }}
          reloadDataOnSuccess={async () => {
            await refetch();
          }}
          autoCloseOnSuccess={true}
        />
      )}

      {/* Modais de Ações (anteriormente no InvoicesTable) */}
      {(currentInvoiceForModal || (bulkBoletoInvoices && bulkBoletoInvoices.length > 0)) && (
        <ProcessBoletoModal
          open={processBoletoOpen}
          onClose={handleProcessBoletoClose}
          invoice={bulkBoletoInvoices?.length ? null : currentInvoiceForModal}
          invoices={bulkBoletoInvoices && bulkBoletoInvoices.length > 0 ? bulkBoletoInvoices : undefined}
          onSuccess={() => {
            void refetch();
          }}
        />
      )}
      {viewInvoice && (
        <InvoiceViewModal
          open={viewModalOpen}
          onClose={handleCloseViewModal}
          invoice_id={viewInvoice.id}
          //onEdit={handleEditClick}
          onReceivePayment={handlePaymentFromView}
          onPrintSingleInvoiceWithSweet={handlePrintSingleInvoiceWithSweet}
          //onCancelInvoice={handleMassCancel}
          onEmitirNfcom={handleEmitirNfcomClick}
        />
      )}
      {selectedInvoiceForEdit && (
        <InvoiceEditForm open={editFormOpen} onClose={handleEditClose} onSubmit={handleEditSave} invoice={selectedInvoiceForEdit} title="Editar Fatura" />
      )}
      <EmitirNfcomModal open={emitirNfcomOpen} onClose={handleEmitirNfcomClose} faturaId={selectedInvoiceForNfcom?.id ?? null} onSuccess={() => refetch()} />
      {selectedInvoiceForRecibo && (
        <ReciboModal open={reciboOpen} onClose={handleReciboClose} invoiceId={selectedInvoiceForRecibo} />
      )}
    </Box>
  );
};

export default FaturasTabContent; 